// frontend/src/utils/auditorApi.js
// Shared API helpers for the auditor portal.
// Centralises paginated-fetch logic and error handling so every auditor
// sub-component behaves consistently.

/**
 * Fetch ALL pages from a DRF-paginated endpoint.
 *
 * Handles three response shapes:
 *   1. Plain array (non-paginated)
 *   2. DRF PageNumberPagination { count, next, results }
 *   3. Empty / error — returns [] and logs the failure.
 *
 * Errors are caught internally so a single failing endpoint never
 * crashes `Promise.all` and never leaves the rest of the data empty.
 *
 * @param {import('axios').AxiosInstance} authAxios
 * @param {string} url  Relative API path, e.g. 'invoices/'
 * @returns {Promise<Array>}
 */
export async function fetchAllPages(authAxios, url) {
  let allResults = [];
  let nextUrl = url;
  let guard = 0;

  try {
    while (nextUrl && guard < 100) {
      guard++;
      const resp = await authAxios.get(nextUrl);
      const data = resp?.data;

      // 1. Plain array (non-paginated endpoint)
      if (Array.isArray(data)) {
        allResults = data;
        break;
      }

      // 2. Paginated response
      if (data?.results) {
        allResults = allResults.concat(data.results);
      }
      nextUrl = data?.next || null;

      // Normalise absolute next-page URLs to relative paths so axios
      // uses the configured baseURL instead of hard-coding the host.
      //
      // [WF-B12] u.pathname is the FULL path including the API prefix
      // (e.g. "/api/v1/audit-logs/"), which already duplicates
      // authAxios's own baseURL (also "http://host/api/v1/") — passing
      // that straight to authAxios.get() made every second-page request
      // resolve to ".../api/v1/api/v1/audit-logs/?page=2", a 404. Silently
      // caught by the catch block below, so fetchAllPages returned
      // whatever it had collected so far (page 1 only — CustomPagination's
      // page_size=10) instead of erroring visibly. Any endpoint with more
      // than one page of results was silently truncated to its first 10
      // rows for every Auditor Portal screen that uses fetchAllPages — the
      // Activity Log tab happened to make this obvious (10 shown vs. 448
      // real rows), but every other list tab was equally affected whenever
      // a firm had more than 10 of that resource. Fixed by stripping the
      // baseURL's own path prefix, so nextUrl becomes plain relative path
      // + query — the same shape callers already pass in.
      if (nextUrl && nextUrl.startsWith('http')) {
        try {
          const u = new URL(nextUrl);
          const base = new URL(authAxios.defaults.baseURL, window.location.origin);
          const relativePath = u.pathname.startsWith(base.pathname)
            ? u.pathname.slice(base.pathname.length)
            : u.pathname.replace(/^\//, '');
          nextUrl = relativePath + u.search;
        } catch {
          break;
        }
      }
    }
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || err.message;
    console.error(`[Auditor] fetchAllPages("${url}") failed — ${status || 'network error'}: ${detail}`);
    // Return whatever we collected (may be [])
  }

  if (allResults.length === 0) {
    console.warn(`[Auditor] fetchAllPages("${url}") returned 0 results`);
  } else {
    console.log(`[Auditor] fetchAllPages("${url}") → ${allResults.length} records`);
  }

  return allResults;
}

/**
 * Convenience wrapper for report-style (non-paginated) endpoints.
 * Returns the response data or null on error.
 *
 * @param {import('axios').AxiosInstance} authAxios
 * @param {string} url
 * @param {object} [params]
 * @returns {Promise<object|null>}
 */
export async function fetchReport(authAxios, url, params = {}) {
  try {
    const resp = await authAxios.get(url, { params });
    return resp.data;
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || err.message;
    console.error(`[Auditor] fetchReport("${url}") failed — ${status || 'network error'}: ${detail}`);
    return null;
  }
}
